import React, { Component } from 'react';
import { Button, Form, Modal, Row, Col, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { GoCheck, GoX, GoWorkflow, GoSync, GoClock } from 'react-icons/go';
import Select from "react-select";
import AceEditor from "react-ace";
import './create.css';

function getTimezoneOptions() {
  const options = [{ value: "UTC", label: "UTC" }];
  try {
    const timezones = Intl.supportedValuesOf('timeZone');
    for (const tz of timezones) {
      options.push({ value: tz, label: tz });
    }
  } catch (err) {
    // Fallback: UTC-only list
  }
  return options;
}

const TIMEZONE_OPTIONS = getTimezoneOptions();

export default class ScheduleModal extends Component {
  constructor(props) {
    super(props);
    this.form = React.createRef();
    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      enabled: true,
      repeat: "recurring_cron",
      cronExpression: "",
      invocationTime: "",
      timezone: "",
      startDate: "",
      endDate: "",
      targetType: "workflow",
      orgId: "",
      blobId: "",
      revisionId: "",
      sessionId: "",
      workflowAlias: "",
      description: "",
      inputData: JSON.stringify([], null, 2)
    };
  }

  // Strip trailing Z and seconds/ms to get datetime-local input format.
  // Used for start/end dates that come back from the server as "YYYY-MM-DDTHH:MM:SSZ".
  utcStringToDatetimeLocal(s) {
    if ( !s ) return "";
    const trimmed = s.endsWith("Z") ? s.slice(0, -1) : s;
    return trimmed.slice(0, 16);
  }

  // Pass through datetime-local input value (no timezone conversion).
  // Used for invocation_time which is in the schedule's timezone, not UTC.
  naiveDatetimeStringToDatetimeLocal(s) {
    if ( !s ) return "";
    return s.slice(0, 16);
  }

  onEnter = () => {
    if ( this.props.schedule ) {
      const schedule = this.props.schedule;
      const target = schedule.invocation_target || {};
      this.setState({
        enabled: schedule.enabled !== false,
        repeat: schedule.repeat || "recurring_cron",
        cronExpression: schedule.cron_expression || "",
        invocationTime: this.naiveDatetimeStringToDatetimeLocal(schedule.invocation_time),
        timezone: schedule.timezone || "",
        startDate: this.utcStringToDatetimeLocal(schedule.start_date),
        endDate: this.utcStringToDatetimeLocal(schedule.end_date),
        targetType: schedule.invocation_target_type || "workflow",
        orgId: target.org_id || "",
        blobId: target.blob_id || target.blob_alias || "",
        revisionId: target.revision_id || "",
        sessionId: target.session_id || "",
        workflowAlias: target.workflow_alias || "",
        description: target.description || "",
        inputData: JSON.stringify(target.input_data || [], null, 2)
      });
    } else {
      this.setState(this.getInitialState());
    }
  }

  isValidInputData() {
    try {
      JSON.parse(this.state.inputData);
      return true;
    } catch ( error ) {
      return false;
    }
  }

  // User enters start/end dates in UTC; append Z suffix. Input format from
  // datetime-local is "YYYY-MM-DDTHH:MM" (seconds defaulted to :00).
  datetimeLocalToUtcString(dateString) {
    if ( !dateString ) {
      return null;
    }
    const withSeconds = dateString.length === 16 ? `${dateString}:00` : dateString;
    return `${withSeconds}Z`;
  }

  // Invocation time is in the schedule's own timezone (not UTC). Send as a
  // naive ISO datetime with no trailing Z.
  datetimeLocalToNaiveString(dateString) {
    if ( !dateString ) {
      return null;
    }
    return dateString.length === 16 ? `${dateString}:00` : dateString;
  }

  handleSubmit = () => {
    if ( !this.form.current.reportValidity() ) {
      return;
    }
    let inputData = null;
    try {
      inputData = JSON.parse(this.state.inputData);
    } catch ( error ) {
      return;
    }

    const schedule = {
      enabled: this.state.enabled,
      repeat: this.state.repeat,
      timezone: this.state.timezone,
      startDate: this.datetimeLocalToUtcString(this.state.startDate),
      endDate: this.datetimeLocalToUtcString(this.state.endDate),
      invocationTarget: {
        org_id: this.state.orgId,
        blob_id: this.state.blobId,
        revision_id: this.state.revisionId,
        session_id: this.state.sessionId,
        workflow_alias: this.state.workflowAlias,
        input_data: inputData,
        description: this.state.description
      }
    };
    if ( "recurring_cron" === this.state.repeat ) {
      schedule.cronExpression = this.state.cronExpression;
    } else {
      schedule.invocationTime = this.datetimeLocalToNaiveString(this.state.invocationTime);
    }
    this.props.handleSubmit(schedule);
    this.props.onHide();
  }

  render() {
    const isEdit = !!this.props.schedule;
    const title = isEdit ? "Edit Schedule" : "Create a New Schedule";
    const submitLabel = isEdit ? "Save" : "Create";

    const enabledOptions = [
      { name: "Enabled", value: true, icon: <GoCheck/> },
      { name: "Disabled", value: false, icon: <GoX/> }
    ];

    const repeatOptions = [
      { name: "Recurring (Cron)", value: "recurring_cron", icon: <GoSync/> },
      { name: "One-time", value: "one_time", icon: <GoClock/> }
    ];

    const targetOptions = [
      { name: "Workflow", value: "workflow", icon: <GoWorkflow/> }
    ];

    return (
      <Modal
        {...this.props}
        onEnter={this.onEnter}
        onExited={this.onEnter}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form ref={this.form} onSubmit={(event) => {
            event.preventDefault();
            this.handleSubmit();
          }}>
            <Form.Group>
              <Form.Label>
                <strong>State</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <ButtonGroup toggle size="sm" className="scheduler-toggle">
                    {enabledOptions.map((option, idx) => (
                      <ToggleButton
                        key={idx}
                        id={`enabled-${idx}`}
                        type="radio"
                        variant="light"
                        name="enabled"
                        value={String(option.value)}
                        checked={this.state.enabled === option.value}
                        onChange={() => this.setState({ enabled: option.value })}
                      >
                        <span style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
                          {option.icon} {option.name}
                        </span>
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </Col>
              </Row>
            </Form.Group>

            <hr/>

            <Form.Group>
              <Form.Label>
                <strong>Repeat Type</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <ButtonGroup toggle size="sm" className="scheduler-toggle">
                    {repeatOptions.map((option, idx) => (
                      <ToggleButton
                        key={idx}
                        id={`repeat-${idx}`}
                        type="radio"
                        variant="light"
                        name="repeat"
                        value={option.value}
                        checked={this.state.repeat === option.value}
                        onChange={() => this.setState({ repeat: option.value })}
                      >
                        <span style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
                          {option.icon} {option.name}
                        </span>
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </Col>
              </Row>
            </Form.Group>


            {"recurring_cron" === this.state.repeat &&
              <Form.Group>
                <Form.Label>
                  <strong>Cron Expression</strong> <span className="text-muted">(required)</span>
                </Form.Label>
                <Row>
                  <Col sm={12}>
                    <Form.Control
                      type="text" size="sm" required
                      value={this.state.cronExpression}
                      onChange={(e) => this.setState({ cronExpression: e.target.value })}
                      placeholder="0 9 * * MON-FRI *"
                    />
                  </Col>
                </Row>
                <Form.Text muted>
                  6-field cron format: minute hour day-of-month month day-of-week year
                </Form.Text>
              </Form.Group>
            }
            {"one_time" === this.state.repeat &&
              <Form.Group>
                <Form.Label>
                  <strong>Invocation Time</strong> <span className="text-muted">(required)</span>
                </Form.Label>
                <Row>
                  <Col sm={12}>
                    <Form.Control
                      type="datetime-local" size="sm" required
                      value={this.state.invocationTime}
                      onChange={(e) => this.setState({ invocationTime: e.target.value })}
                    />
                  </Col>
                </Row>
                <Form.Text muted>
                  The time to invoke the schedule, interpreted in the schedule's timezone.
                </Form.Text>
              </Form.Group>
            }


            <Form.Group>
              <Form.Label>
                <strong>Timezone</strong> <span className="text-muted">(required)</span>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <Select
                    options={TIMEZONE_OPTIONS}
                    value={TIMEZONE_OPTIONS.find(opt => opt.value === this.state.timezone)}
                    onChange={(option) => this.setState({ timezone: option ? option.value : "" })}
                    placeholder="Select timezone..."
                    isClearable={false}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                </Col>
              </Row>
            </Form.Group>


            {"recurring_cron" === this.state.repeat &&
              <>
                <Row>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>Start Date</strong>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local" size="sm"
                        value={this.state.startDate}
                        onChange={(e) => this.setState({ startDate: e.target.value })}
                      />
                      <Form.Text muted>
                        Entered and stored in UTC.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>End Date</strong>
                      </Form.Label>
                      <Form.Control
                        type="datetime-local" size="sm"
                        value={this.state.endDate}
                        onChange={(e) => this.setState({ endDate: e.target.value })}
                      />
                      <Form.Text muted>
                        Entered and stored in UTC.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

              </>
            }

            <hr/>
            <Form.Group>
              <Form.Label>
                <strong>Target Type</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <ButtonGroup toggle size="sm" className="scheduler-toggle">
                    {targetOptions.map((option, idx) => (
                      <ToggleButton
                        key={idx}
                        id={`target-${idx}`}
                        type="radio"
                        variant="light"
                        name="targetType"
                        value={option.value}
                        checked={this.state.targetType === option.value}
                        onChange={() => this.setState({ targetType: option.value })}
                      >
                        <span style={{display: "inline-flex", alignItems: "center", gap: "6px"}}>
                          {option.icon} {option.name}
                        </span>
                      </ToggleButton>
                    ))}
                  </ButtonGroup>
                </Col>
              </Row>
            </Form.Group>


            {"workflow" === this.state.targetType &&
              <>
                <Row>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>Org ID</strong> <span className="text-muted">(required)</span>
                      </Form.Label>
                      <Form.Control
                        type="text" size="sm" required
                        value={this.state.orgId}
                        onChange={(e) => this.setState({ orgId: e.target.value })}
                        placeholder="Organization ID or alias"
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>Blob ID</strong> <span className="text-muted">(required)</span>
                      </Form.Label>
                      <Form.Control
                        type="text" size="sm" required
                        value={this.state.blobId}
                        onChange={(e) => this.setState({ blobId: e.target.value })}
                        placeholder="Blob ID or alias"
                      />
                    </Form.Group>
                  </Col>
                </Row>


                <Row>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>Revision ID</strong> <span className="text-muted">(required)</span>
                      </Form.Label>
                      <Form.Control
                        type="text" size="sm" required
                        value={this.state.revisionId}
                        onChange={(e) => this.setState({ revisionId: e.target.value })}
                        placeholder="Revision ID"
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group>
                      <Form.Label>
                        <strong>Session ID</strong> <span className="text-muted">(required)</span>
                      </Form.Label>
                      <Form.Control
                        type="text" size="sm" required
                        value={this.state.sessionId}
                        onChange={(e) => this.setState({ sessionId: e.target.value })}
                        placeholder="Session ID"
                      />
                    </Form.Group>
                  </Col>
                </Row>


                <Form.Group>
                  <Form.Label>
                    <strong>Workflow Alias</strong> <span className="text-muted">(required)</span>
                  </Form.Label>
                  <Row>
                    <Col sm={12}>
                      <Form.Control
                        type="text" size="sm" required
                        value={this.state.workflowAlias}
                        onChange={(e) => this.setState({ workflowAlias: e.target.value })}
                        placeholder="Workflow Alias"
                      />
                    </Col>
                  </Row>
                </Form.Group>

                <Form.Group>
                  <Form.Label>
                    <strong>Description</strong>
                  </Form.Label>
                  <Row>
                    <Col sm={12}>
                      <Form.Control
                        type="text" size="sm"
                        value={this.state.description}
                        onChange={(e) => this.setState({ description: e.target.value })}
                        placeholder="Optional description for this schedule"
                      />
                    </Col>
                  </Row>
                </Form.Group>

                <Form.Group>
                  <Form.Label>
                    <strong>Input Data</strong>
                  </Form.Label>
                  <Row>
                    <Col sm={12}>
                      <AceEditor
                        name="input-data-editor" width="100%" height="100px" mode="json" theme="monokai"
                        wrapEnabled="true"
                        value={this.state.inputData}
                        onChange={(newValue) => {
                          this.setState({inputData: newValue});
                        }}
                      />
                    </Col>
                  </Row>
                  <Form.Text muted>
                    JSON input data for workflow execution.
                  </Form.Text>
                  <Form.Control.Feedback type="invalid"
                    style={{"display": this.isValidInputData() ? "none" : "block"}}>
                    Invalid JSON provided
                  </Form.Control.Feedback>
                </Form.Group>
              </>
            }
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          <Button variant="success" onClick={this.handleSubmit}>{submitLabel}</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}
